import { CubejsApi } from '@cubejs-client/core';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import _ from 'lodash';
import {
  BlueberryFeatures,
  BlueberryFlowerStageFeatures,
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  buildRowsideEstimatedCountQuery,
  RowsideEstimatedCountCSVResponse,
  rowsideEstimatedCountCSVResponseSchema,
} from './buildRowsideEstimatedCountQuery';

/**
 * Pre-processes the CSV output data by grouping and aggregating it based on certain criteria.
 *
 * @param {RowsideEstimatedCountCSVResponse[]} data - The array of data items to be processed.
 * @return {Array} - The processed and aggregated data.
 */
const preprocessCSVOutput = (
  data: RowsideEstimatedCountCSVResponse[]
): ProcessedData[] => {
  // Group the data based on a specific criteria
  const groupedData = Object.entries(
    _.groupBy(
      data
        .filter((el) =>
          [...BlueberryFeatures, ...BlueberryFlowerStageFeatures].find(
            (f) => f === el[CubeDimensionEnum.className]
          )
        )
        .map((el) => ({
          ...el,
          // Generate an id using specific properties of the data item
          id: `${el[CubeDimensionEnum.rowsideRowNum]}-${
            el[CubeDimensionEnum.rowsideViewDirection]
          }`,
        })),
      (el) => el.id
    )
  ).map(([key, entry]) => {
    // Calculate the estimated counts for each blueberry feature
    const blueberryEstimatedCounts = Object.fromEntries(
      BlueberryFeatures.map((feature) => [
        feature,
        entry.find((el) => el[CubeDimensionEnum.className] === feature)?.[
          CubeMeasureEnum.detectionFactEstimatedCount
        ] || null,
      ])
    );

    // Calculate the total count
    const totalBerries = Object.values(blueberryEstimatedCounts).reduce(
      (acc: number, el) => {
        if (!el || typeof el !== 'number') return acc;
        return acc + el;
      },
      0
    );

    const flowerEstimatedCounts = Object.fromEntries(
      [FeatureEnum.flower, ...BlueberryFlowerStageFeatures].map((feature) => [
        feature,
        entry.find((el) => el[CubeDimensionEnum.className] === feature)?.[
          CubeMeasureEnum.detectionFactEstimatedCount
        ] || null,
      ])
    );
    const hasFlowerStages = BlueberryFlowerStageFeatures.some(
      (flowerStage) => flowerEstimatedCounts[flowerStage]
    );

    let totalFlowers = 0;
    if (hasFlowerStages) {
      const flowerEstimatedCountsStagesOnly = Object.fromEntries(
        Object.entries(flowerEstimatedCounts).filter(([key]) =>
          BlueberryFlowerStageFeatures.includes(key as FeatureEnum)
        )
      );

      totalFlowers = Object.values(flowerEstimatedCountsStagesOnly).reduce(
        (acc: number, el) => {
          if (!el || typeof el !== 'number') return acc;
          return acc + el;
        },
        0
      );
    } else {
      totalFlowers = flowerEstimatedCounts[FeatureEnum.flower] || 0;
    }

    return {
      id: key,
      rowsideNum: entry[0][CubeDimensionEnum.rowsideRowNum],
      viewDirection: entry[0][CubeDimensionEnum.rowsideViewDirection],
      [FeatureEnum.blueberry_Blue]:
        blueberryEstimatedCounts[FeatureEnum.blueberry_Blue],
      [FeatureEnum.blueberry_Green]:
        blueberryEstimatedCounts[FeatureEnum.blueberry_Green],
      [FeatureEnum.blueberry_Purple]:
        blueberryEstimatedCounts[FeatureEnum.blueberry_Purple],
      [FeatureEnum.blueberry_Yellow]:
        blueberryEstimatedCounts[FeatureEnum.blueberry_Yellow],
      total: totalBerries,
      [FeatureEnum.flower_closed]:
        flowerEstimatedCounts[FeatureEnum.flower_closed],
      [FeatureEnum.flower_open]: flowerEstimatedCounts[FeatureEnum.flower_open],
      [FeatureEnum.flower_petalfall]:
        flowerEstimatedCounts[FeatureEnum.flower_petalfall],
      totalFlowers: totalFlowers,
    };
  });

  return groupedData;
};

/**
 * Interface for the processed and aggregated data.
 */
interface ProcessedData {
  id: string;
  rowsideNum: number;
  viewDirection: string;
  [FeatureEnum.blueberry_Blue]: number | null;
  [FeatureEnum.blueberry_Green]: number | null;
  [FeatureEnum.blueberry_Purple]: number | null;
  [FeatureEnum.blueberry_Yellow]: number | null;
  total: number;
  [FeatureEnum.flower_closed]: number | null;
  [FeatureEnum.flower_open]: number | null;
  [FeatureEnum.flower_petalfall]: number | null;
  totalFlowers: number | null;
}

/**
 * Export the given data to a CSV file.
 *
 * @param {PreprocessedCSVOutputItem[]} data - The data to be exported.
 * @param {string} blockName - The name of the block.
 * @param {string} scanDate - The date of the scan.
 *  @param {Function} t - The translation function.
 */
const exportCSV = (
  data: ProcessedData[],
  blockName: string,
  scanDate: string,
  t: (key: string, options?: { ns: string }) => string
): void => {
  const columns = [
    t('Rowside Number'),
    t('View Direction'),
    t(FeatureEnum.flower_closed),
    t(FeatureEnum.flower_open),
    t(FeatureEnum.flower_petalfall),
    t('Total Flowers'),
    t('Green'),
    t('Pink'),
    t('Purple'),
    t('Blue'),
    t('Total Berries'),
  ];

  const exportData = data.map((row) => [
    row.rowsideNum,
    row.viewDirection,
    row[FeatureEnum.flower_closed],
    row[FeatureEnum.flower_open],
    row[FeatureEnum.flower_petalfall],
    row.totalFlowers,
    row[FeatureEnum.blueberry_Green],
    row[FeatureEnum.blueberry_Yellow],
    row[FeatureEnum.blueberry_Purple],
    row[FeatureEnum.blueberry_Blue],
    row.total,
  ]);

  const options = {
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    useTextFile: false,
    useBom: true,
    filename: `Block-${blockName}-${scanDate}-Rowside-Estimated-Count`,
    headers: columns,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
};

/**
 * Generates a CSV export of the estimated count for a given block rowside.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.blockName - The name of the block.
 * @param {string} params.scanId - The ID of the scan.
 * @param {FeatureEnum} serviceName - The service name to use in the query (e.g. 'blueberry'. Called modelName in database).
 * @param {string} serviceVersion - The service version (e.g. '14').
 * @param {CubejsApi} params.cubejsApi - The CubejsApi instance.
 * @param {Function} t - The translation function, to be passed down to the exportCSV function.
 * @return {void} - This function does not return a value.
 */
export const exportBlockRowsideEstimatedCountCSV = async ({
  blockName,
  scanId,
  scanDate,
  serviceName,
  serviceVersion,
  cubejsApi,
  t,
}: {
  blockName: string;
  scanId: string;
  scanDate: string;
  serviceName: FeatureEnum;
  serviceVersion: string;
  cubejsApi: CubejsApi;
  t: (key: string, options?: { ns: string }) => string;
}) => {
  const query = buildRowsideEstimatedCountQuery(
    scanId,
    serviceName,
    serviceVersion
  );

  const result = await cubejsApi.load(query);
  const rawData = result.rawData();
  const validatedData: RowsideEstimatedCountCSVResponse[] = [];

  rawData.forEach((dataItem) => {
    try {
      rowsideEstimatedCountCSVResponseSchema.validateSync(dataItem, {
        abortEarly: false,
      });
      validatedData.push(rowsideEstimatedCountCSVResponseSchema.cast(dataItem));
    } catch (validationError) {
      console.error(
        'Validation Error for data item:',
        dataItem,
        'Error:',
        validationError
      );
    }
  });

  exportCSV(preprocessCSVOutput(validatedData), blockName, scanDate, t);
};
